import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminService } from 'app/javascript/services/admin';
import type { AdminListParams } from 'app/javascript/types/admin';

export const adminOverviewKey = ['admin', 'overview'];
export const adminUsersKey = ['admin', 'users'];
export const adminCompaniesKey = ['admin', 'companies'];
export const adminFailedPhotosKey = ['admin', 'failed-photos'];
export const adminCompanyHealthKey = (slug: string) => ['admin', 'company-health', slug];
export const adminCompanyMembersKey = (slug: string) => ['admin', 'company-members', slug];
export const adminCompanyPhotosKey = (slug: string) => ['admin', 'company-photos', slug];

export function useAdminOverview() {
  return useQuery({ queryKey: adminOverviewKey, queryFn: adminService.overview });
}

// keepPreviousData holds the current page on screen while the next one loads,
// so paging and typing don't blank the table between keystrokes.
export function useAdminUsers({ search = '', page = 1 }: AdminListParams = {}) {
  return useQuery({
    queryKey: [...adminUsersKey, search, page],
    queryFn: () => adminService.users({ search, page }),
    placeholderData: keepPreviousData,
  });
}

export function useAdminCompanies({ search = '', page = 1 }: AdminListParams = {}) {
  return useQuery({
    queryKey: [...adminCompaniesKey, search, page],
    queryFn: () => adminService.companies({ search, page }),
    placeholderData: keepPreviousData,
  });
}

export function useAdminCompanyHealth(slug: string) {
  return useQuery({
    queryKey: adminCompanyHealthKey(slug),
    queryFn: () => adminService.companyHealth(slug),
    enabled: Boolean(slug),
  });
}

export function useAdminCompanyMembers(slug: string) {
  return useQuery({
    queryKey: adminCompanyMembersKey(slug),
    queryFn: () => adminService.companyMembers(slug),
    enabled: Boolean(slug),
  });
}

export function useAdminCompanyTrainingPhotos(slug: string) {
  return useQuery({
    queryKey: adminCompanyPhotosKey(slug),
    queryFn: () => adminService.companyTrainingPhotos(slug),
    enabled: Boolean(slug),
  });
}

// A grant changes the plan the academy sits on, so the response replaces the
// cached health snapshot the page is rendering right now.
export function useGrantPlan(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (platformPlanId: number) => adminService.grantPlan(slug, platformPlanId),
    onSuccess: (health) => queryClient.setQueryData(adminCompanyHealthKey(slug), health),
  });
}

export function useRevokeGrant(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => adminService.revokeGrant(slug),
    onSuccess: (health) => queryClient.setQueryData(adminCompanyHealthKey(slug), health),
  });
}

export function useGrantFacialUploadBonus(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ idempotencyKey }: { idempotencyKey: string }) =>
      adminService.grantFacialUploadBonus(slug, idempotencyKey),
    onSuccess: (health) => queryClient.setQueryData(adminCompanyHealthKey(slug), health),
  });
}

export function useAdminFailedPhotos() {
  return useQuery({ queryKey: adminFailedPhotosKey, queryFn: adminService.failedPhotos });
}

export function useReprocessFailedPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.reprocessFailedPhoto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminFailedPhotosKey });
      queryClient.invalidateQueries({ queryKey: adminOverviewKey });
    },
  });
}
